import {FC} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import Address from 'components/Address';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TableStatic from 'components/Table/TableStatic';
import lodash from 'lodash';
import {useBackend} from 'state/backend';
import {AddressType} from 'types/address';
import {toIntWithCommas} from 'utils/number';
import {BillingDetailsBackend} from './types';

// As per the spec, the address will be displayed as either Japanese or Overseas format.
// Westernized Japanese format is converted to Japanese format
const standardizeAddressType = (billingAddress: AddressType): AddressType => {
    if (billingAddress?.addressFormat === 'jp_western') {
        const billingAddressClone = lodash.cloneDeep(billingAddress);
        billingAddressClone.addressFormat = 'jp_default';

        return billingAddressClone;
    }

    return billingAddress;
};

const Details: FC = () => {
    const {t} = useTranslation('Billing.Details');

    const {
        data: {
            isBillingMethodChangeButtonShown,
            isPlanCancelButtonShown,
            partnerName,
            rows,
            showGracePeriod,
        },
    } = useBackend<BillingDetailsBackend>();

    return (
        <div className="flex flex-col gap-2">
            <NavigationTabs.Billing selection="billingDetails" />
            <TableStatic>
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column colSpan={3} id="table-username">
                            {partnerName}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {rows.gracePeriodPlan && (
                        <TableStatic.Row id="table-grace-period-plan">
                            <TableStatic.Cell>
                                {t('plan')} {t('gracePeriod')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.gracePeriodPlan}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.gracePeriodAddons && (
                        <TableStatic.Row id="table-grace-period-addons">
                            <TableStatic.Cell className="align-top">
                                {t('addons')} {t('gracePeriod')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.gracePeriodAddons.length === 0 && (
                                    <span>&mdash;</span>
                                )}
                                {rows.gracePeriodAddons.length > 0 && (
                                    <ul className="mt-0">
                                        {rows.gracePeriodAddons.map(
                                            (gracePeriodAddon) => (
                                                <li key={gracePeriodAddon}>
                                                    {gracePeriodAddon}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.newPlanStart && (
                        <TableStatic.Row id="table-new-plan-start">
                            <TableStatic.Cell>
                                {t('newPlanStart')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.newPlanStart}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.plan && (
                        <TableStatic.Row id="table-plan">
                            <TableStatic.Cell>{t('plan')}</TableStatic.Cell>
                            <TableStatic.Cell>{rows.plan}</TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change plan"
                                    className="mr-1.5"
                                    href="/billing/prices/"
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t('planChangeButton')}
                                </LinkButton>
                                {isPlanCancelButtonShown && (
                                    <LinkButton
                                        aria-label="cancel plan"
                                        href="/billing/cancel-confirm/"
                                        kind="destructive"
                                        size="xs"
                                    >
                                        {t('planCancelButton')}
                                    </LinkButton>
                                )}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.sendingOrders && (
                        <TableStatic.Row id="table-send-orders">
                            <TableStatic.Cell>
                                {t('sendOrdersLabel')} <br />
                                <small className="opacity-60">
                                    {t('resetDate')}{' '}
                                    {rows.sendingOrders.resetDate}
                                </small>
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <Trans
                                    i18nKey="sendOrdersValue"
                                    ns="Billing.Details"
                                    values={{
                                        limit: rows.sendingOrders.limit,
                                        sent: rows.sendingOrders.sentCount,
                                    }}
                                />
                                {rows.sendingOrders.sentCount >=
                                    rows.sendingOrders.limit && (
                                    <>
                                        <br />
                                        <div
                                            className="text-sm text-gold-600"
                                            role="alert"
                                        >
                                            {t('sendOrdersWarning')}
                                        </div>
                                    </>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change limit"
                                    href="/billing/change-sendingorder-limit/"
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t('sendOrdersButton')}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.teamMembers && rows.teamMembers.limit && (
                        <TableStatic.Row id="table-team-members">
                            <TableStatic.Cell>
                                {t('teamMembersLabel')}{' '}
                                {showGracePeriod && t('gracePeriod')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <Trans
                                    i18nKey="teamMembersValue"
                                    ns="Billing.Details"
                                    values={{
                                        limit: rows.teamMembers.limit,
                                        members: rows.teamMembers.memberCount,
                                    }}
                                />
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change member limit"
                                    href="/billing/prices/"
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t('teamMembersButton')}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.clients && rows.clients.limit && (
                        <TableStatic.Row id="table-clients">
                            <TableStatic.Cell>
                                {t('clientsLabel')}{' '}
                                {showGracePeriod && t('gracePeriod')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <Trans
                                    i18nKey="clientsValue"
                                    ns="Billing.Details"
                                    values={{
                                        clients: rows.clients.clientCount,
                                        limit: rows.clients.limit,
                                    }}
                                />
                                {rows.clients.clientCount >=
                                    rows.clients.limit && (
                                    <>
                                        <br />
                                        <div
                                            className="text-sm text-gold-600"
                                            role="alert"
                                        >
                                            {t('clientsWarning')}
                                        </div>
                                    </>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="change member limit"
                                    href="/billing/prices/"
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t('clientsButton')}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.addons && (
                        <TableStatic.Row id="table-addons">
                            <TableStatic.Cell className="align-top">
                                {t('addons')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.addons.length === 0 && (
                                    <span>&mdash;</span>
                                )}
                                {rows.addons.length > 0 && (
                                    <ul className="mt-0">
                                        {rows.addons.map((addon) => (
                                            <li key={addon}>{addon}</li>
                                        ))}
                                    </ul>
                                )}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.virtualAccounts && (
                        <TableStatic.Row id="table-virtual-accounts">
                            <TableStatic.Cell className="align-top">
                                {t('virtualAccounts')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <Trans
                                    i18nKey="virtualAccountsValue"
                                    ns="Billing.Details"
                                    values={{
                                        count: rows.virtualAccounts
                                            .virtualAccountCount,
                                        limit: rows.virtualAccounts.limit,
                                    }}
                                />
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.price && (
                        <TableStatic.Row id="table-price">
                            <TableStatic.Cell>{t('price')}</TableStatic.Cell>
                            <TableStatic.Cell>{rows.price}</TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.billingFrequency && (
                        <TableStatic.Row id="table-billing-frequency">
                            <TableStatic.Cell>
                                {t('billingFrequency')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.billingFrequency}
                            </TableStatic.Cell>
                            <TableStatic.Cell />
                        </TableStatic.Row>
                    )}
                    {rows.billingMethod && (
                        <TableStatic.Row id="table-billing-method">
                            <TableStatic.Cell>
                                {t('billingMethod')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {rows.billingMethod}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {isBillingMethodChangeButtonShown && (
                                    <LinkButton
                                        aria-label="billing method"
                                        href="/billing/change-payment-details/"
                                        kind="secondary"
                                        size="xs"
                                    >
                                        {t('changeBillingMethod')}
                                    </LinkButton>
                                )}
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {rows.billingAddress && (
                        <TableStatic.Row id="table-billing-contact">
                            <TableStatic.Cell>
                                {t('billingContact')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <Address
                                    address={standardizeAddressType(
                                        rows.billingAddress,
                                    )}
                                />
                                {rows.billingEmail}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="billing contact"
                                    href="/billing/change-payment-details/"
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t('changeBillingContact')}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    <TableStatic.Row id="table-receipts">
                        <TableStatic.Cell>{t('receipts')}</TableStatic.Cell>
                        <TableStatic.Cell />
                        <TableStatic.Cell>
                            <LinkButton
                                aria-label="Request receipts"
                                href="/billing/request-receipts/"
                                kind="secondary"
                                size="xs"
                            >
                                {t('receiptsButton')}
                            </LinkButton>
                        </TableStatic.Cell>
                    </TableStatic.Row>
                    {rows.mailPoints !== undefined && (
                        <TableStatic.Row id="table-mailpoints">
                            <TableStatic.Cell>
                                {t('mailPoints')}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                {toIntWithCommas(rows.mailPoints)}
                            </TableStatic.Cell>
                            <TableStatic.Cell>
                                <LinkButton
                                    aria-label="Buy Mailpoints"
                                    href="/billing/mailpoints/"
                                    kind="secondary"
                                    size="xs"
                                >
                                    {t('mailPointsButton')}
                                </LinkButton>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
        </div>
    );
};

export default Details;
